import { resolveSidebarItem } from './sidebarSelection';
/**
 * NEXUMA — SidebarRenderer
 *
 * Renders the navigation sidebar from sidebarConfig.js,
 * filtering sections and items based on the current user's permissions.
 *
 * Drop-in replacement for the inline NavList in Dashboard.js
 * (Dashboard.js still works without it — both can coexist).
 *
 * Features:
 * - Permission-filtered sections + items
 * - Collapsible sections with active-state detection
 * - Badge support (e.g. "NEW")
 * - Collapsed icon-only mode
 * - Tooltip labels in icon-only mode
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import List             from '@mui/material/List';
import ListItem         from '@mui/material/ListItem';
import ListItemButton   from '@mui/material/ListItemButton';
import ListItemIcon     from '@mui/material/ListItemIcon';
import ListItemText     from '@mui/material/ListItemText';
import Collapse         from '@mui/material/Collapse';
import Divider          from '@mui/material/Divider';
import Box              from '@mui/material/Box';
import Typography       from '@mui/material/Typography';
import Tooltip          from '@mui/material/Tooltip';
import Chip             from '@mui/material/Chip';
import IconButton       from '@mui/material/IconButton';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import StarIcon         from '@mui/icons-material/Star';
import StarBorderIcon   from '@mui/icons-material/StarBorder';

// ── Icon map ─────────────────────────────────────────────────────────────────
// Maps string icon names (from config) to MUI icon components.
// Add entries here as new configs reference new icons.
import HomeIcon               from '@mui/icons-material/Home';
import SpeedIcon              from '@mui/icons-material/Speed';
import BadgeIcon              from '@mui/icons-material/Badge';
import GroupsIcon             from '@mui/icons-material/Groups';
import AccountTreeIcon        from '@mui/icons-material/AccountTree';
import FolderOpenIcon         from '@mui/icons-material/FolderOpen';
import ArticleIcon            from '@mui/icons-material/Article';
import DescriptionIcon        from '@mui/icons-material/Description';
import TrendingUpIcon         from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import ScheduleIcon           from '@mui/icons-material/Schedule';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import PendingActionsIcon     from '@mui/icons-material/PendingActions';
import FactCheckIcon          from '@mui/icons-material/FactCheck';
import EventNoteIcon          from '@mui/icons-material/EventNote';
import HomeWorkIcon           from '@mui/icons-material/HomeWork';
import BeachAccessIcon        from '@mui/icons-material/BeachAccess';
import EventAvailableIcon     from '@mui/icons-material/EventAvailable';
import AssessmentIcon         from '@mui/icons-material/Assessment';
import HowToRegIcon           from '@mui/icons-material/HowToReg';
import GavelIcon              from '@mui/icons-material/Gavel';
import CalendarMonthIcon      from '@mui/icons-material/CalendarMonth';
import AssignmentIcon         from '@mui/icons-material/Assignment';
import KanbanIcon             from '@mui/icons-material/ViewKanban';
import WorkOutlineIcon        from '@mui/icons-material/WorkOutline';
import BusinessIcon           from '@mui/icons-material/Business';
import TimerIcon              from '@mui/icons-material/Timer';
import PeopleAltIcon          from '@mui/icons-material/PeopleAlt';
import PersonSearchIcon       from '@mui/icons-material/PersonSearch';
import RecordVoiceOverIcon    from '@mui/icons-material/RecordVoiceOver';
import SchoolIcon             from '@mui/icons-material/School';
import AccountBalanceIcon     from '@mui/icons-material/AccountBalance';
import PaymentsIcon           from '@mui/icons-material/Payments';
import CardGiftcardIcon       from '@mui/icons-material/CardGiftcard';
import SavingsIcon            from '@mui/icons-material/Savings';
import ReceiptLongIcon        from '@mui/icons-material/ReceiptLong';
import BarChartIcon           from '@mui/icons-material/BarChart';
import DateRangeIcon          from '@mui/icons-material/DateRange';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import FolderSpecialIcon      from '@mui/icons-material/FolderSpecial';
import MenuBookIcon           from '@mui/icons-material/MenuBook';
import LibraryBooksIcon       from '@mui/icons-material/LibraryBooks';
import TuneIcon               from '@mui/icons-material/Tune';
import NotificationsIcon      from '@mui/icons-material/Notifications';
import ManageSearchIcon       from '@mui/icons-material/ManageSearch';
import SecurityIcon           from '@mui/icons-material/Security';
import SettingsIcon           from '@mui/icons-material/Settings';
import QrCodeScannerIcon      from '@mui/icons-material/QrCodeScanner';
import EditCalendarIcon       from '@mui/icons-material/EditCalendar';
import TableChartIcon         from '@mui/icons-material/TableChart';
import ExtensionIcon          from '@mui/icons-material/Extension';
import InboxIcon              from '@mui/icons-material/MoveToInbox';
import ForumIcon              from '@mui/icons-material/ForumRounded';
import Inventory2Icon         from '@mui/icons-material/Inventory2';
import HelpOutlineIcon        from '@mui/icons-material/HelpOutline';
import QueryStatsIcon         from '@mui/icons-material/QueryStats';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import MoreTimeIcon           from '@mui/icons-material/MoreTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentIndIcon      from '@mui/icons-material/AssignmentInd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SummarizeIcon          from '@mui/icons-material/Summarize';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import EuroIcon               from '@mui/icons-material/Euro';
import HistoryIcon            from '@mui/icons-material/History';
import GppMaybeIcon           from '@mui/icons-material/GppMaybe';
import PolicyIcon             from '@mui/icons-material/Policy';

import Avatar                  from '@mui/material/Avatar';
import usePermissions         from '../../hooks/usePermissions';
import useFavorites           from '../../hooks/useFavorites';
import SIDEBAR_CONFIG, { SIDEBAR_EMPLOYEE_CONFIG } from '../../config/permissions/sidebarConfig';
import { useTranslation }      from 'react-i18next';
import configService           from '../../services/configService';

const ICON_MAP = {
  HomeIcon, SpeedIcon, BadgeIcon, GroupsIcon, AccountTreeIcon,
  FolderOpenIcon, ArticleIcon, DescriptionIcon, TrendingUpIcon,
  EmojiEventsIcon, ScheduleIcon, DashboardCustomizeIcon, PendingActionsIcon,
  FactCheckIcon, EventNoteIcon, HomeWorkIcon, BeachAccessIcon,
  EventAvailableIcon, AssessmentIcon, HowToRegIcon, GavelIcon,
  CalendarMonthIcon, AssignmentIcon, KanbanIcon, WorkOutlineIcon,
  BusinessIcon, TimerIcon, PeopleAltIcon, PersonSearchIcon,
  RecordVoiceOverIcon, SchoolIcon, AccountBalanceIcon, PaymentsIcon,
  CardGiftcardIcon, SavingsIcon, ReceiptLongIcon, BarChartIcon, DateRangeIcon,
  WarningAmberIcon, FolderSpecialIcon, MenuBookIcon, LibraryBooksIcon,
  TuneIcon, NotificationsIcon, ManageSearchIcon, SecurityIcon, SettingsIcon,
  QrCodeScannerIcon, EditCalendarIcon, TableChartIcon, ExtensionIcon, InboxIcon,
  ForumIcon, HelpOutlineIcon, Inventory2Icon,
  // Raportet — ikonat e grupeve dhe të raporteve individuale
  QueryStatsIcon, AccessTimeIcon, MoreTimeIcon, AccountBalanceWalletIcon,
  AssignmentIndIcon, AssignmentTurnedInIcon, SummarizeIcon, CheckCircleIcon, EuroIcon, HistoryIcon,
  GppMaybeIcon, PolicyIcon, StarIcon,
};

// ─────────────────────────────────────────────────────────────────────────────
// Nav geometry
//
// Three levels live in a 240px rail (seksion → nën-grup → raport). They share
// ONE rhythm so the eye reads depth from indentation alone, not from a soup of
// different row heights and type sizes:
//
//   [ic] Raportet                    ⌄   L1  icon 18px · label 48px · 34px row
//        [ic] Paga & Kostot          ⌄   L2  icon 44px · label 72px · 32px row
//              │  Kosto pune             L3  rail 55px · label 72px · 30px row
//
// L3 carries no icon: at that depth the glyphs were four grey money pictograms
// that said nothing the label didn't, and they cost 28px of a 240px rail. The
// guide rail replaces them — it shows the parent link the icons never did, and
// L3 labels align under the L2 label instead of starting at a third indent.
const NAV = {
  iconBox:   22,   // icon tile — same on every level so the columns line up
  iconGlyph: 16,
  iconCol:   28,   // ListItemIcon width at L2 (tile + 6px slack before the label)
  font:      '0.8125rem',
  // L3 rail sits on the centre of the L2 icon tile: 8px row padding + 11px.
  railOffset: '19px',
  railGap:    '8px',
};

// The theme's focus ring sits 2px OUTSIDE the element. On a 12px-radius nav row
// that reads as a combobox outline rather than focus, so nav rows pull it inside.
const navFocusRing = { '&:focus-visible': { outlineOffset: '-2px' } };

// Shigjeta e palosjes: rrotullim + zbehje, të dyja nga shkalla e temës.
const chevronTransition = (theme) => [
  `transform ${theme.motion?.base ?? 200}ms ${theme.motion?.easing ?? 'ease'}`,
  `opacity ${theme.motion?.fast ?? 160}ms ${theme.motion?.easing ?? 'ease'}`,
].join(', ');

function Icon({ name, sx }) {
  const C = ICON_MAP[name];
  return C ? <C sx={sx} /> : null;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function SidebarRenderer({ drawerOpen = true, onClose }) {
  const location = useLocation();
  const { getVisibleMenuItems, isWorkerOnly } = usePermissions();

  const rawConfig   = isWorkerOnly ? SIDEBAR_EMPLOYEE_CONFIG : SIDEBAR_CONFIG;
  const visibleMenu = getVisibleMenuItems(rawConfig);

  const { favorites, isFavorite, toggleFavorite } = useFavorites();

  // Flat index of every navigable leaf module the user is allowed to see.
  // Used both to resolve favorites (only show permitted ones) and to attach the
  // star toggle. Keyed by item id.
  const leafIndex = React.useMemo(() => {
    const out = {};
    const walk = (items) =>
      items.forEach((it) => {
        if (it.children) walk(it.children);
        else if (it.to) out[it.id] = it;
      });
    walk(visibleMenu);
    return out;
  }, [visibleMenu]);

  // ── Dynamic modules from DB ───────────────────────────────────────────────
  // Custom modules are an admin/management surface — hide them entirely from
  // worker-only (employee) users.
  const [dynamicModules, setDynamicModules] = React.useState([]);
  React.useEffect(() => {
    if (isWorkerOnly) { setDynamicModules([]); return; }
    let cancelled = false;
    configService.getModules().then(r => {
      if (!cancelled && r.success) {
        setDynamicModules((r.data || []).filter(m => m.is_active && m.is_custom));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isWorkerOnly]);

  const [preferredItemId, setPreferredItemId] = React.useState(null);
  const activeItemId = resolveSidebarItem(visibleMenu, location, preferredItemId);
  const isItemActive = React.useCallback(
    (item) => Boolean(item && item.id === activeItemId),
    [activeItemId]
  );

  // Recursive: seksioni "Raportet" ka nën-grupe, ndaj aktiviteti mund të jetë
  // dy nivele poshtë (seksion → grup → raport).
  const sectionHasActive = React.useCallback(
    (item) => {
      const walk = (node) => (node.children ? node.children.some(walk) : isItemActive(node));
      return walk(item);
    },
    [isItemActive]
  );

  const [openSections, setOpenSections] = React.useState(() =>
    visibleMenu.map((item) => sectionHasActive(item))
  );

  React.useEffect(() => {
    setOpenSections(visibleMenu.map((item) => sectionHasActive(item)));
  }, [location.pathname, location.search, activeItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (i) =>
    setOpenSections((prev) => prev.map((v, idx) => (idx === i ? !v : false)));

  // Resolve favorites to permitted leaf items, preserving the user's order.
  const favoriteItems = favorites.map((id) => leafIndex[id]).filter(Boolean);

  return (
    <Box
      data-tour="sidebar"
      onClickCapture={(event) => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[data-sidebar-id]');
        if (link) setPreferredItemId(link.dataset.sidebarId);
      }}
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: 'background.paper',
        overflowY: 'auto',
        overflowX: 'hidden',
        pt: 1,
        pb: 2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.200', borderRadius: 2 },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.300' },
      }}
    >
      {/* ── Favoritet — modulet e zgjedhura nga përdoruesi ─────────────────── */}
      {favoriteItems.length > 0 && (
        <>
          {drawerOpen ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mx: 1.25, mt: 0.75, px: 1, py: 0.85, gap: 1 }}>
                <Box
                  sx={{
                    width: 22, height: 22, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, color: 'warning.main',
                    '& svg': { fontSize: 15 },
                  }}
                >
                  <StarIcon />
                </Box>
                <Typography
                  variant="overline"
                  sx={{ flex: 1, color: 'text.secondary', lineHeight: 1, fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.66rem' }}
                >
                  Favoritet
                </Typography>
              </Box>
              <List disablePadding sx={{ mx: 1.25, mt: 0.25, mb: 0.5, pl: 3.25 }}>
                {favoriteItems.map((child) => (
                  <NavChild
                    key={`fav-${child.id}`}
                    item={child}
                    active={isItemActive(child)}
                    onClose={onClose}
                    isFav={isFavorite(child.id)}
                    onToggleFav={toggleFavorite}
                  />
                ))}
              </List>
            </>
          ) : (
            <>
              <List disablePadding sx={{ px: 0.5 }}>
                {favoriteItems.map((child) => (
                  <IconChild
                    key={`fav-${child.id}`}
                    item={child}
                    active={isItemActive(child)}
                    onClose={onClose}
                  />
                ))}
              </List>
              <Divider sx={{ mx: 1.5, my: 0.5, borderColor: 'divider' }} />
            </>
          )}
        </>
      )}

      {visibleMenu.map((item, gi) => {
        const isSection = Boolean(item.section || item.children);
        const isOpen    = openSections[gi] ?? false;
        const active    = sectionHasActive(item);

        // ── Flat item (no children) ────────────────────────────────────────
        if (!isSection) {
          return (
            <FlatItem
              key={item.id}
              item={item}
              active={isItemActive(item)}
              drawerOpen={drawerOpen}
              onClose={onClose}
            />
          );
        }

        // ── Section with children ──────────────────────────────────────────
        return (
          <React.Fragment key={item.id}>
            {!drawerOpen && gi > 0 && (
              <Divider sx={{ mx: 1.5, my: 0.5, borderColor: 'divider' }} />
            )}

            {/* Section header — buton i vërtetë, jo <Box onClick>: ishte i
                paarritshëm me tastierë dhe pa `aria-expanded`, pikërisht ajo që
                docs/notes/ui_conventions.md e ndalon shprehimisht. */}
            {drawerOpen && (
              <Box
                component="button"
                type="button"
                onClick={() => toggleSection(gi)}
                aria-expanded={isOpen}
                aria-controls={`navsection-${item.id}`}
                data-tour={`nav-${item.id}`}
                sx={{
                  display:    'flex',
                  alignItems: 'center',
                  width:      'calc(100% - 20px)',
                  textAlign:  'left',
                  font:       'inherit',
                  border:     0,
                  mx:         1.25,
                  mt:         gi === 0 ? 0.5 : 0.25,
                  px:         1,
                  py:         0.55,
                  minHeight:  34,
                  borderRadius: 1.5,
                  cursor:     'pointer',
                  userSelect: 'none',
                  gap:        1,
                  bgcolor:    active && isOpen ? 'primary.soft' : 'transparent',
                  transition: 'background-color 0.15s ease',
                  '&:hover':  { bgcolor: active && isOpen ? 'primary.soft' : 'grey.50' },
                  '&:hover .nav-chevron': { opacity: 1 },
                  ...navFocusRing,
                }}
              >
                {/* Group icon — <span>, jo <div>: përmbajtja e një <button>
                    duhet të jetë phrasing content, përndryshe markup-i s'është valid. */}
                <Box
                  component="span"
                  sx={{
                    width:          NAV.iconBox,
                    height:         NAV.iconBox,
                    borderRadius:   1,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    flexShrink:     0,
                    color:          active ? 'primary.main' : 'text.disabled',
                    transition:     'color 0.15s',
                    '& svg':        { fontSize: NAV.iconGlyph },
                  }}
                >
                  <Icon name={item.icon} />
                </Box>

                <SectionLabel item={item} active={active} />

                <ExpandMoreIcon
                  className="nav-chevron"
                  sx={(theme) => ({
                    fontSize:   16,
                    color:      'text.disabled',
                    opacity:    isOpen ? 1 : 0.55,
                    transition: chevronTransition(theme),
                    transform:  isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    flexShrink: 0,
                  })}
                />
              </Box>
            )}

            {/* Section items */}
            {drawerOpen ? (
              <Collapse in={isOpen} timeout={180} unmountOnExit>
                <List
                  id={`navsection-${item.id}`}
                  disablePadding
                  sx={{
                    mx: 1.25,
                    mt: 0.25,
                    mb: 0.5,
                    pl: 3.25,
                  }}
                >
                  {(item.children || []).map((child) =>
                    child.children ? (
                      <NavGroup
                        key={child.id}
                        item={child}
                        isItemActive={isItemActive}
                        onClose={onClose}
                        isFavorite={isFavorite}
                        onToggleFav={toggleFavorite}
                      />
                    ) : (
                      <NavChild
                        key={child.id}
                        item={child}
                        active={isItemActive(child)}
                        onClose={onClose}
                        isFav={child.to ? isFavorite(child.id) : undefined}
                        onToggleFav={child.to ? toggleFavorite : undefined}
                      />
                    )
                  )}
                </List>
              </Collapse>
            ) : (
              // Icon-only mode: show all children as icon buttons. Nën-grupet
              // (p.sh. grupet e raporteve) rrafshohen — në shiritin e ngushtë
              // s'ka vend për një nivel të dytë palosjeje.
              <List disablePadding sx={{ px: 0.5 }}>
                {flattenLeaves(item.children).map((child) => (
                  <IconChild
                    key={child.id}
                    item={child}
                    active={isItemActive(child)}
                    onClose={onClose}
                  />
                ))}
              </List>
            )}
          </React.Fragment>
        );
      })}

      {/* ── Dynamic custom modules (MODULE section) — hidden for now ───────── */}
      {false && dynamicModules.length > 0 && (
        <>
          {drawerOpen && (
            <Box sx={{ mx: 1.25, mt: 2, mb: 0.5, px: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.disabled', lineHeight: 1 }}>
                Module
              </Typography>
            </Box>
          )}
          {!drawerOpen && <Divider sx={{ mx: 1.5, my: 0.5, borderColor: 'divider' }} />}
          <List disablePadding sx={drawerOpen ? { mx: 1.25, pl: 3.25 } : { px: 0.5 }}>
            {dynamicModules.map(mod => {
              const to = `/m/${mod.key}`;
              const active = location.pathname === to || location.pathname.startsWith(to + '/');
              const iconComp = ICON_MAP[mod.icon];

              if (!drawerOpen) {
                return (
                  <Tooltip key={mod.id} title={mod.name} placement="right" arrow>
                    <span>
                      <ListItem disablePadding>
                        <ListItemButton
                          component={Link} to={to} onClick={onClose} selected={active}
                          sx={{
                            py: 0.5, mx: 0.5, borderRadius: 1.5, minHeight: 34,
                            justifyContent: 'center',
                            color: active ? 'primary.main' : 'text.disabled',
                            '&.Mui-selected': { bgcolor: 'primary.soft', color: 'primary.main', '&:hover': { bgcolor: 'primary.soft' } },
                            '&:hover:not(.Mui-selected)': { bgcolor: 'grey.50' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0, color: 'inherit', '& svg': { fontSize: 18 } }}>
                            {iconComp ? React.createElement(iconComp, { sx: { fontSize: 18 } }) : (
                              <Avatar sx={{ width: 20, height: 20, fontSize: '0.625rem' }}>{mod.name[0]}</Avatar>
                            )}
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    </span>
                  </Tooltip>
                );
              }

              return (
                <ListItem key={mod.id} disablePadding>
                  <ListItemButton
                    component={Link} to={to} onClick={onClose} selected={active}
                    sx={{
                      py: 0.55, px: 1, borderRadius: 1.5, minHeight: 32,
                      position: 'relative',
                      color: active ? 'primary.dark' : 'text.primary',
                      '&.Mui-selected': {
                        bgcolor: 'primary.soft',
                        color: 'primary.dark',
                        '&:hover': { bgcolor: 'primary.soft' },
                        '&::before': {
                          content: '""', position: 'absolute', left: -13, top: 6, bottom: 6,
                          width: 2, borderRadius: 2, backgroundColor: 'primary.main',
                        },
                      },
                      '&:hover:not(.Mui-selected)': { bgcolor: 'grey.50' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 24, color: active ? 'primary.main' : 'text.disabled', '& svg': { fontSize: 15 } }}>
                      {iconComp ? React.createElement(iconComp, { sx: { fontSize: 15 } }) : (
                        <Avatar sx={{ width: 18, height: 18, fontSize: '0.5625rem' }}>{mod.name[0]}</Avatar>
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={mod.name}
                      primaryTypographyProps={{
                        sx: { fontWeight: active ? 600 : 500, fontSize: '0.8125rem', letterSpacing: '-0.005em', lineHeight: 1.35 },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </Box>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

// ── Translation helpers ──────────────────────────────────────────────────────

function useSidebarT(item) {
  const { t } = useTranslation();
  return t(`sidebar.${item.id}`, item.label);
}

function SectionLabel({ item, active }) {
  const label = useSidebarT(item);
  return (
    <Typography
      component="span"
      noWrap
      sx={{
        flex:          1,
        minWidth:      0,
        color:         active ? 'primary.dark' : 'text.primary',
        lineHeight:    1.35,
        fontWeight:    600,
        letterSpacing: '-0.005em',
        fontSize:      NAV.font,
        transition:    'color 0.15s',
      }}
    >
      {label}
    </Typography>
  );
}

/**
 * Props for a sidebar leaf. Most items navigate; a few only fire an action —
 * "Helper" opens the support panel in place rather than routing anywhere,
 * because the panel is a global overlay, not a page.
 */
function itemLinkProps(item, onClose) {
  if (item.disabled) return {};
  if (item.action) {
    return {
      component: 'div',
      role: 'button',
      onClick: () => { window.dispatchEvent(new CustomEvent(item.action)); if (onClose) onClose(); },
    };
  }
  return { component: Link, to: item.to, onClick: onClose, 'data-sidebar-id': item.id };
}

function FlatItem({ item, active, drawerOpen, onClose }) {
  const label = useSidebarT(item);
  const linkProps = itemLinkProps(item, onClose);
  const btn = (
    <ListItem disablePadding>
      <ListItemButton
        {...linkProps}
        selected={active}
        data-tour={`nav-${item.id}`}
        sx={{
          py:             0.55,
          px:             drawerOpen ? 1 : 0,
          my:             0.25,
          borderRadius:   1.5,
          minHeight:      34,
          justifyContent: drawerOpen ? 'flex-start' : 'center',
          mx:             drawerOpen ? 1.25 : 0.75,
          position:       'relative',
          color:          active ? 'primary.dark' : 'text.primary',
          transition:     'background-color 0.15s ease, color 0.15s ease',
          ...navFocusRing,
          '&.Mui-selected': {
            bgcolor: 'primary.soft',
            color:   'primary.dark',
            '&:hover': { bgcolor: 'primary.soft' },
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            ...(drawerOpen && {
              '&::before': {
                content: '""', position: 'absolute',
                left: -8, top: 8, bottom: 8,
                width: 3, borderRadius: 2,
                backgroundColor: 'primary.main',
              },
            }),
          },
          '&:hover:not(.Mui-selected)': { bgcolor: 'grey.50' },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: drawerOpen ? 30 : 0,
            color: active ? 'primary.main' : 'text.disabled',
            '& svg': { fontSize: drawerOpen ? NAV.iconGlyph : 18 },
          }}
        >
          {drawerOpen ? (
            // Same 22px icon tile as section headers so the icon column lines up.
            <Box sx={{ width: NAV.iconBox, height: NAV.iconBox, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={item.icon} />
            </Box>
          ) : (
            <Icon name={item.icon} />
          )}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={label}
            // minWidth:0 lets the label shrink so long names truncate with an
            // ellipsis on ONE line instead of wrapping to 2–3 ragged lines.
            sx={{ minWidth: 0, my: 0 }}
            primaryTypographyProps={{
              noWrap: true,
              title:  label,   // native tooltip keeps a truncated label readable
              sx: {
                fontWeight:    600,
                fontSize:      NAV.font,
                letterSpacing: '-0.005em',
              },
            }}
          />
        )}
        {drawerOpen && item.badge && (
          <Chip
            label={item.badge}
            size="small"
            // flexShrink:0 so the badge never squeezes the label into wrapping.
            sx={{
              height:     18,
              fontSize:   '0.5625rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              bgcolor:    'primary.main',
              color:      'primary.contrastText',
              ml:         0.75,
              flexShrink: 0,
              '& .MuiChip-label': { px: 0.625 },
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
  if (!drawerOpen) {
    return (
      <Tooltip title={label} placement="right" arrow>
        <span>{btn}</span>
      </Tooltip>
    );
  }
  return btn;
}

/** Të gjitha gjethet navigueshme të një liste, duke hapur nën-grupet. */
function flattenLeaves(items) {
  return (items || []).flatMap((it) => (it.children ? flattenLeaves(it.children) : [it]));
}

/**
 * Nën-grup i palosshëm brenda një seksioni — p.sh. "Paga & Kostot" nën
 * "Raportet". Hapet vetvetiu kur faqja aktive ndodhet brenda tij, që
 * përdoruesi të shohë ku është pa e kërkuar grupin me dorë.
 *
 * Koka e grupit ndërtohet SI fëmijët e tij (i njëjti kuti-ikonë 22px, e njëjta
 * madhësi shkronjash) — ndryshon vetëm pesha dhe ngjyra, sepse ajo është
 * etiketë kontejneri, jo destinacion. Më parë ishte 0.78rem me ikonë 15px në
 * kolonë tjetër, prandaj koka dhe fëmijët dukeshin si të një niveli.
 */
function NavGroup({ item, isItemActive, onClose, isFavorite, onToggleFav }) {
  const label     = useSidebarT(item);
  const children  = item.children || [];
  const hasActive = children.some(isItemActive);
  const [open, setOpen] = React.useState(hasActive);
  const listId = `navgroup-${item.id}`;

  React.useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={listId}
          sx={{
            mx:           0,
            py:           0.4,
            px:           1,
            borderRadius: 1.5,
            minHeight:    32,
            color:        hasActive ? 'primary.dark' : 'text.secondary',
            '&:hover':    { bgcolor: 'grey.50' },
            '&:hover .nav-chevron': { opacity: 1 },
            ...navFocusRing,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: NAV.iconCol,
              color:    hasActive ? 'primary.main' : 'text.disabled',
              '& svg':  { fontSize: NAV.iconGlyph },
            }}
          >
            <Box sx={{ width: NAV.iconBox, height: NAV.iconBox, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={item.icon} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{ minWidth: 0, my: 0 }}
            primaryTypographyProps={{
              noWrap: true,
              title:  label,
              sx: {
                fontWeight:    600,
                fontSize:      NAV.font,
                letterSpacing: '-0.005em',
                lineHeight:    1.35,
                color:         'inherit',
              },
            }}
          />
          <ExpandMoreIcon
            className="nav-chevron"
            sx={(theme) => ({
              fontSize:   15,
              color:      'text.disabled',
              opacity:    open ? 1 : 0.55,
              transition: chevronTransition(theme),
              transform:  open ? 'rotate(0deg)' : 'rotate(-90deg)',
              flexShrink: 0,
            })}
          />
        </ListItemButton>
      </ListItem>

      {/* Fëmijët varen nga një shina 1px që bie mbi qendrën e ikonës së grupit —
          lidhja prind–fëmijë ishte krejt e padukshme më parë. */}
      <Collapse in={open} timeout={160} unmountOnExit>
        <List
          id={listId}
          disablePadding
          sx={{
            ml:          NAV.railOffset,
            pl:          NAV.railGap,
            borderLeft:  '1px solid',
            borderColor: 'divider',
          }}
        >
          {children.map((child) => (
            <NavChild
              key={child.id}
              item={child}
              active={isItemActive(child)}
              onClose={onClose}
              nested
              isFav={child.to ? isFavorite(child.id) : undefined}
              onToggleFav={child.to ? onToggleFav : undefined}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

/**
 * Gjethe navigimi. `nested` = niveli i tretë (brenda një NavGroup): pa ikonë,
 * sepse etiketa e grupit sipër dhe shina majtas e japin kontekstin, dhe
 * etiketa e fituar 28px gjerësi në një shirit 240px.
 */
function NavChild({ item, active, onClose, isFav, onToggleFav, nested = false }) {
  const label = useSidebarT(item);
  const linkProps = itemLinkProps(item, onClose);
  const canFav = typeof onToggleFav === 'function';
  return (
    <ListItem
      disablePadding
      sx={canFav ? { '&:hover .fav-star': { opacity: 1 } } : undefined}
      secondaryAction={
        canFav ? (
          <IconButton
            className="fav-star"
            size="small"
            aria-label={isFav ? 'Hiq nga favoritet' : 'Shto te favoritet'}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(item.id); }}
            sx={{
              p: 0.25,
              color: isFav ? 'warning.main' : 'text.disabled',
              opacity: isFav ? 1 : 0,
              transition: 'opacity 0.15s, color 0.15s',
              '&:hover': { color: 'warning.main', bgcolor: 'transparent' },
            }}
          >
            {isFav ? <StarIcon sx={{ fontSize: 15 }} /> : <StarBorderIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        ) : undefined
      }
    >
      <ListItemButton
        {...linkProps}
        selected={active}
        disabled={item.disabled}
        sx={{
          mx:           0,
          py:           0.4,
          px:           1,
          ...(canFav && { pr: 3.5 }),
          borderRadius: 1.5,
          minHeight:    nested ? 30 : 32,
          position:     'relative',
          color:        active ? 'primary.dark' : 'text.primary',
          ...navFocusRing,
          '&.Mui-selected': {
            bgcolor: 'primary.soft',
            color:   'primary.dark',
            '&:hover': { bgcolor: 'primary.soft' },
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            // Shenja e aktivit ekziston vetëm aty ku shenjon diçka: te niveli
            // i tretë bie SAKTËSISHT mbi shinën. Te niveli i dytë u hoq — rrinte
            // pezull mbi 13px hapësirë boshe pa treguar asgjë; tinti
            // `primary.soft` me tekst e ikonë teal e thonë vetë se cili është aktiv.
            ...(nested && {
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '-9px',
                top: 5,
                bottom: 5,
                width: 2,
                borderRadius: 2,
                backgroundColor: 'primary.main',
              },
            }),
          },
          '&:hover:not(.Mui-selected)': { bgcolor: 'grey.50' },
          ...(item.disabled && {
            opacity:       0.4,
            pointerEvents: 'none',
            cursor:        'default',
          }),
        }}
      >
        {!nested && (
          <ListItemIcon
            sx={{
              minWidth: NAV.iconCol,
              color: active ? 'primary.main' : 'text.disabled',
              '& svg': { fontSize: NAV.iconGlyph },
            }}
          >
            <Box sx={{ width: NAV.iconBox, height: NAV.iconBox, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={item.icon} />
            </Box>
          </ListItemIcon>
        )}
        <ListItemText
          primary={label}
          sx={{ minWidth: 0, my: 0 }}
          primaryTypographyProps={{
            noWrap: true,
            title:  label,
            sx: {
              fontWeight:    active ? 600 : 500,
              fontSize:      NAV.font,
              letterSpacing: '-0.005em',
              lineHeight:    1.35,
              color:         'inherit',
            },
          }}
        />
        {item.badge && (
          <Chip
            label={item.badge}
            size="small"
            sx={{
              height:     18,
              fontSize:   '0.625rem',
              fontWeight: 600,
              bgcolor:    active ? 'background.paper' : 'primary.main',
              color:      active ? 'primary.dark' : 'primary.contrastText',
              border:     active ? 1 : 0,
              borderColor: active ? 'primary.soft' : 'transparent',
              ml:         0.5,
              flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}

function IconChild({ item, active, onClose }) {
  const label = useSidebarT(item);
  const btn = (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        data-sidebar-id={item.id}
        to={item.to}
        onClick={onClose}
        selected={active}
        sx={{
          py:             0.5,
          mx:             0.5,
          borderRadius:   1.5,
          minHeight:      34,
          justifyContent: 'center',
          color:          active ? 'primary.main' : 'text.disabled',
          '&.Mui-selected': {
            bgcolor: 'primary.soft',
            color:   'primary.main',
            '&:hover': { bgcolor: 'primary.soft' },
            '& .MuiListItemIcon-root': { color: 'primary.main' },
          },
          '&:hover:not(.Mui-selected)': { bgcolor: 'grey.50' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, color: 'inherit', '& svg': { fontSize: 18 } }}>
          <Icon name={item.icon} />
        </ListItemIcon>
      </ListItemButton>
    </ListItem>
  );
  return (
    <Tooltip title={label} placement="right" arrow>
      <span>{btn}</span>
    </Tooltip>
  );
}
